import React, { useContext } from 'react';
import { connect, DispatchProp } from 'react-redux';

import { ActionName, ItemName } from '@dndbeyond/character-components/es';
import {
    AbilityLookup,
    Action,
    ActionUtils,
    BaseInventoryContract,
    characterActions,
    Constants,
    EntityValueLookup,
    InfusionUtils,
    HelperUtils,
    InventoryLookup,
    ItemUtils,
    RuleData,
    rulesEngineSelectors,
    EntityUtils,
    CharacterTheme,
    InventoryManager,
} from '@dndbeyond/character-rules-engine/es';

import { sidebarActions } from '../../../actions/sidebar';
import ActionDetail from '../../../components/ActionDetail';
import PaneInitFailureContent from '../../../components/Pane/PaneInitFailureContent';
import { SidebarHeader } from '../../../components/Sidebar';
import { InventoryManagerContext } from '../../../managers/InventoryManagerContext';
import { appEnvSelectors } from '../../../selectors';
import { SharedAppState } from '../../../stores/typings';
import { PaneComponentEnum, PaneUtils } from '../../../utils';
import { PaneIdentifiersAction } from '../../../utils/PaneIdentifier/typings';

interface Props extends DispatchProp {
    actions: Array<Action>;
    ruleData: RuleData;
    entityValueLookup: EntityValueLookup;
    abilityLookup: AbilityLookup;
    inventoryLookup: InventoryLookup;
    identifiers: PaneIdentifiersAction | null;
    isReadonly: boolean;
    proficiencyBonus: number;
    theme: CharacterTheme;
    inventoryManager: InventoryManager;
}
interface State {
    action: Action | null;
}
class ActionPane extends React.PureComponent<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = this.generateStateData(props);
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
        const { actions, identifiers } = this.props;

        if (actions !== prevProps.actions || identifiers !== prevProps.identifiers) {
            this.setState(this.generateStateData(this.props));
        }
    }

    generateStateData = (props: Props): State => {
        const { actions, identifiers } = props;

        let foundAction: Action | null | undefined = null;
        if (identifiers !== null) {
            foundAction = actions.find(
                (action) =>
                    identifiers.id === ActionUtils.getMappingId(action) &&
                    identifiers.entityTypeId === ActionUtils.getMappingEntityTypeId(action),
            );
        }

        return {
            action: foundAction ? foundAction : null,
        };
    };

    handleCustomDataUpdate = (key: number, value: string, source: string): void => {
        const { action } = this.state;
        const { dispatch } = this.props;

        if (!action) {
            return;
        }

        dispatch(
            characterActions.valueSet(
                key,
                value,
                source,
                ActionUtils.getMappingId(action),
                ActionUtils.getMappingEntityTypeId(action),
            ),
        );
    };

    handleRemoveCustomizations = (): void => {
        const { action } = this.state;
        const { dispatch } = this.props;

        if (action) {
            const mappingId = ActionUtils.getMappingId(action);
            const mappingEntityTypeId = ActionUtils.getMappingEntityTypeId(action);

            if (mappingId !== null && mappingEntityTypeId !== null) {
                dispatch(characterActions.actionCustomizationsDelete(mappingId, mappingEntityTypeId));
            }
        }
    };

    handleLimitedUseSet = (uses: number): void => {
        const { dispatch, inventoryManager } = this.props;
        const { action } = this.state;

        if (!action) {
            return;
        }

        const mappingId = ActionUtils.getMappingId(action);
        const mappingEntityTypeId = ActionUtils.getMappingEntityTypeId(action);

        if (mappingId === null || mappingEntityTypeId === null) {
            return;
        }

        let dataOriginType = ActionUtils.getDataOriginType(action);

        if (dataOriginType === Constants.DataOriginTypeEnum.ITEM) {
            const dataOrigin = ActionUtils.getDataOrigin(action);
            const item = dataOrigin.primary as BaseInventoryContract;
            inventoryManager.handleChargesSet({ item, used: uses });
        } else {
            dispatch(characterActions.actionUseSet(mappingId, mappingEntityTypeId, uses, dataOriginType));
        }
    };

    handleParentClick = (): void => {
        const { action } = this.state;
        const { dispatch } = this.props;

        if (!action) {
            return;
        }

        let dataOrigin = ActionUtils.getDataOrigin(action);
        let component = PaneUtils.getDataOriginComponentInfo(dataOrigin);
        if (component.type !== PaneComponentEnum.ERROR_404) {
            dispatch(sidebarActions.paneHistoryPush(component.type, component.identifiers));
        }
    };

    render() {
        const { action } = this.state;
        const { ruleData, entityValueLookup, isReadonly, inventoryLookup, abilityLookup, proficiencyBonus, theme } =
            this.props;

        if (!action) {
            return <PaneInitFailureContent />;
        }

        let name: React.ReactNode = null;
        if (ActionUtils.getName(action) !== '') {
            name = <ActionName theme={theme} action={action} />;
        }

        let parentName: string | null = null;
        let parentClick: (() => void) | undefined = undefined;

        let dataOrigin = ActionUtils.getDataOrigin(action);
        let dataOriginType = ActionUtils.getDataOriginType(action);

        if (dataOriginType === Constants.DataOriginTypeEnum.ITEM) {
            const itemContract = dataOrigin.primary as BaseInventoryContract;
            const itemMappingId = ItemUtils.getMappingId(itemContract);
            const item = HelperUtils.lookupDataOrFallback(inventoryLookup, itemMappingId);

            if (item !== null) {
                let infusion = ItemUtils.getInfusion(item);
                if (infusion) {
                    parentName = `Infusion: ${InfusionUtils.getName(infusion)}`;
                    parentClick = this.handleParentClick;
                }

                if (name === null) {
                    name = <ItemName theme={theme} item={item} onClick={this.handleParentClick} />;
                }
            }
        } else {
            if (dataOrigin.primary) {
                parentName = EntityUtils.getDataOriginName(dataOrigin);
                parentClick = this.handleParentClick;
            }
        }

        return (
            <div className="ct-action-pane" key={ActionUtils.getUniqueKey(action)}>
                <SidebarHeader parent={parentName} onParentClick={parentClick}>
                    {name}
                </SidebarHeader>
                <ActionDetail
                    theme={theme}
                    action={action}
                    abilityLookup={abilityLookup}
                    showCustomize={dataOriginType !== Constants.DataOriginTypeEnum.ITEM}
                    onCustomDataUpdate={this.handleCustomDataUpdate}
                    onCustomizationsRemove={this.handleRemoveCustomizations}
                    ruleData={ruleData}
                    entityValueLookup={entityValueLookup}
                    inventoryLookup={inventoryLookup}
                    isReadonly={isReadonly}
                    onLimitedUseSet={this.handleLimitedUseSet}
                    proficiencyBonus={proficiencyBonus}
                />
            </div>
        );
    }
}

function mapStateToProps(state: SharedAppState) {
    return {
        actions: rulesEngineSelectors.getActions(state),
        abilityLookup: rulesEngineSelectors.getAbilityLookup(state),
        ruleData: rulesEngineSelectors.getRuleData(state),
        entityValueLookup: rulesEngineSelectors.getCharacterValueLookupByEntity(state),
        inventoryLookup: rulesEngineSelectors.getInventoryLookup(state),
        isReadonly: appEnvSelectors.getIsReadonly(state),
        proficiencyBonus: rulesEngineSelectors.getProficiencyBonus(state),
        theme: rulesEngineSelectors.getCharacterTheme(state),
    };
}

const ActionPaneContainer = (props) => {
    const { inventoryManager } = useContext(InventoryManagerContext);
    return <ActionPane inventoryManager={inventoryManager} {...props} />;
};

export default connect(mapStateToProps)(ActionPaneContainer);
